/**
 * Phase 17 — in-chat quote card.
 *
 * Renders four chat-message kinds:
 *  - `quote`             — active quote with subtotal range + materials
 *  - `quote_superseded`  — dimmed, "[Superseded · $X]" badge, no CTA
 *  - `expired_quote`     — EXPIRED banner + "Request fresh quote" CTA
 *  - `approval`          — "Quote approved" + inline "Pay deposit" CTA
 *
 * Offer countdown surfaces when `estimate.current_offer` is set;
 * customer never sees `offer_history[]`.
 */

import { useState } from "react";

import { refreshEstimate } from "@starz/shared/api/sdk.gen";
import type {
  ChatMessage,
  Estimate,
  Offer,
  QuotePayload,
} from "@starz/shared/api/types.gen";

import { OfferCountdown } from "./OfferCountdown";

interface Props {
  message: Pick<ChatMessage, "kind" | "content" | "quote_payload">;
  /** Live Estimate row so the card can render `current_offer` countdown
   * (admin offers happen out-of-band from this chat message, so the
   * payload's snapshot is just an initial paint). */
  estimate?: Estimate | null;
  /** Customer's pay handler — invoked from `approval` cards. Receives
   * the estimate_id of the approved quote. */
  onPayDeposit?: (estimateId: string) => void;
  /** Called after a successful `refreshEstimate` so the parent can
   * re-fetch chat messages and surface the fresh quote card. */
  onRefreshed?: () => void;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function renderSubtotalRange(p: QuotePayload): string {
  if (p.subtotal_low_cents === p.subtotal_high_cents) {
    return formatCents(p.subtotal_low_cents);
  }
  return `${formatCents(p.subtotal_low_cents)}–${formatCents(p.subtotal_high_cents)}`;
}

function OfferLine({ offer }: { offer: Offer }) {
  return (
    <div className="mt-2 flex flex-col gap-1 rounded-lg bg-starz-cream/60 px-3 py-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-starz-gold">
          Contractor offer
        </span>
        <span className="font-mono text-lg text-starz-navy">
          {formatCents(offer.final_cents)}
        </span>
      </div>
      {offer.discount_cents ? (
        <div className="text-xs text-warm-gray">
          Includes a {formatCents(offer.discount_cents)} discount
        </div>
      ) : null}
      <OfferCountdown expiresAtIso={offer.expires_at} />
    </div>
  );
}

export function QuoteCard({ message, estimate, onPayDeposit, onRefreshed }: Props) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const payload = message.quote_payload;
  const kind = message.kind ?? "text";

  // The active offer always comes from the live Estimate row (not the
  // payload snapshot) so countdowns stay accurate as the admin edits.
  const activeOffer = estimate?.current_offer ?? null;

  if (!payload) {
    return (
      <div className="rounded-2xl border border-starz-gold/20 bg-starz-cream/40 px-5 py-3 text-sm text-warm-gray">
        {message.content}
      </div>
    );
  }

  const isSuperseded = kind === "quote_superseded";
  const isExpired = kind === "expired_quote";
  const isApproval = kind === "approval";

  const containerClass = isSuperseded
    ? "rounded-2xl border border-starz-gold/15 bg-starz-cream/30 px-5 py-3 opacity-60"
    : isExpired
      ? "rounded-2xl border-2 border-starz-brick/40 bg-starz-brick/5 px-5 py-4"
      : isApproval
        ? "rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 px-5 py-4"
        : "rounded-2xl border border-starz-gold/30 bg-white px-5 py-4 shadow-sm";

  return (
    <div className={containerClass}>
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-starz-gold">
          {isSuperseded
            ? "Superseded"
            : isExpired
              ? "Expired"
              : isApproval
                ? "Approved"
                : "Quote"}
        </span>
        <span className="font-mono text-xl text-starz-navy">
          {renderSubtotalRange(payload)}
        </span>
      </div>
      {!isSuperseded && payload.primary_materials.length > 0 ? (
        <ul className="mt-2 list-disc pl-5 text-xs text-warm-gray">
          {payload.primary_materials.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      ) : null}
      {!isSuperseded && message.content ? (
        <p className="mt-2 text-sm text-starz-charcoal">{message.content}</p>
      ) : null}
      {!isSuperseded && !isExpired && activeOffer ? <OfferLine offer={activeOffer} /> : null}
      {isExpired ? (
        <div className="mt-3 flex flex-col gap-2">
          <p className="text-sm text-starz-charcoal">
            This quote has expired. Catalog prices may have changed —
            request a fresh quote to re-price against today's catalog.
          </p>
          <button
            type="button"
            disabled={refreshing}
            onClick={async () => {
              if (refreshing) return;
              setRefreshing(true);
              setRefreshError(null);
              const result = await refreshEstimate({ estimateId: payload.estimate_id });
              setRefreshing(false);
              if ("error" in result) {
                setRefreshError(result.error.error.message);
                return;
              }
              onRefreshed?.();
            }}
            className="self-start rounded-md bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white hover:opacity-90 disabled:opacity-40"
          >
            {refreshing ? "Requesting…" : "Request fresh quote"}
          </button>
          {refreshError ? (
            <p className="text-xs text-starz-brick">{refreshError}</p>
          ) : null}
        </div>
      ) : null}
      {isApproval ? (
        <button
          type="button"
          onClick={() => onPayDeposit?.(payload.estimate_id)}
          className="mt-3 rounded-md bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white hover:opacity-90"
        >
          Pay deposit
        </button>
      ) : null}
    </div>
  );
}
